import React, { useEffect, useMemo } from "react";
import { Container, Box, Typography, Card, CardContent, CardActions, Button, Alert } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apiClientHooks } from "../bootstrapping/InitApiClient";
import { useProfileAssured } from "../bootstrapping/InitRequireProfile";
import ShowInvites, { useCustomerInvites } from "../components/invites/ShowInvites";

const getCustomerNameFontSize = (name: string): number => {
  const length = name.trim().length

  if (length <= 20) return 1.6
  if (length <= 28) return 1.45
  if (length <= 36) return 1.3
  if (length <= 48) return 1.15
  return 1.02
}

export const useAdminCustomersWithName = (): { customer_id: string, customer_name: string, more_than_shown: boolean }[] | undefined => {
  const profile = useProfileAssured()
  const admin_customer_ids = profile.admin_customer_ids

  // NOTE: For staff, who can see all customers, this might be a long list! (it might even page and cause problems?)
  // TODO: Consider adding a customFiler for 'my-admin-customers' set to true, which then filters based on the user's admin-customer-ids.
  const customers = apiClientHooks.useGetCustomers({
    queries: {
      page_index: '0',
      page_size: '100',
    },
  }, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hour
  })

  const admin_customers = useMemo(() => {
    if (customers.data == null) return undefined
    return customers.data.rows.filter(customer => admin_customer_ids.includes(customer.id))
  }, [admin_customer_ids, customers.data])

  return admin_customers == null
    ? undefined
    : admin_customers.map(customer => {
      const customer_id = customer.id
      const customer_name = customer.name
      // Are there admin_customer_ids which we don't have admin_customers for
      const more_than_shown = admin_customers.length < admin_customer_ids.length
      return { customer_id, customer_name, more_than_shown }
    })
}

export const useSelectedCustomer = (): { customer_id: string, customer_name: string | undefined } | undefined => {
  const { customer_id } = useParams<{ customer_id: string | undefined }>()

  const customers = apiClientHooks.useGetCustomers({
    queries: {
      // We don't worry about the page size because we're filtering by customer_id so should only get one result
      globalFilter: customer_id,
    },
  }, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 60, // 1 hour
    enabled: customer_id != null,
  })

  return customer_id == null
    ? undefined
    : customers.data == null
      ? { customer_id, customer_name: undefined }
      : { customer_id, customer_name: customers.data.rows[0]?.name }
}

export const Element: React.FC = () => {
  const navigate = useNavigate()
  const onClickNavigateToCustomer = (customerId: string): React.MouseEventHandler<HTMLDivElement | HTMLButtonElement> => event => {
    navigate(`/${customerId}/`)
    event.preventDefault()
    event.stopPropagation()
  }

  const { isInitialLoading, customerInvites } = useCustomerInvites()
  const hasCustomerInvites = isInitialLoading
    ? undefined
    : customerInvites.length > 0

  const admin_customers = useAdminCustomersWithName()
  const moreThanShown = admin_customers?.some(customer => customer.more_than_shown) ?? false

  // If we are sure we have no customer invites and there is only one customer, select it automatically
  useEffect(() => {
    if (hasCustomerInvites === false && admin_customers?.length === 1) {
      navigate(`/${admin_customers[0].customer_id}/`)
    }
  }, [hasCustomerInvites, admin_customers, navigate])

  return <>
    <Helmet>
      <title>Select Customer</title>
    </Helmet>
    <Container component="main" maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <ShowInvites noInvites={<></>} />
        <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
          Select Customer
        </Typography>
        <Box
          sx={{
            width: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 280px))',
            justifyContent: 'center',
            gap: 3,
          }}
        >
          {admin_customers?.map(({ customer_id, customer_name }, index) => {
            const onClick = onClickNavigateToCustomer(customer_id)
            const nameFontSizeRem = getCustomerNameFontSize(customer_name)
            return (
              <Card
                key={index}
                variant="outlined"
                sx={{
                  width: 280,
                  height: 200,
                  backgroundColor: '#e9e9e9',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                onClick={onClick}
              >
                <CardContent sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2.5 }}>
                  <Typography
                    component="div"
                    sx={{
                      width: '100%',
                      textAlign: 'center',
                      fontWeight: 600,
                      fontSize: `${nameFontSizeRem}rem`,
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {customer_name}
                  </Typography>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button fullWidth onClick={onClickNavigateToCustomer(customer_id)}>Select</Button>
                </CardActions>
              </Card>
            )
          })}
        </Box>
        {moreThanShown && (
          <Alert severity="warning" sx={{ mt: 3, width: '100%', maxWidth: 920 }}>
            Not all customers are shown. You have admin access to additional customers that are not currently displayed.
          </Alert>
        )}
      </Box>
    </Container>
  </>
}